import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { useLoginLang, localizeError } from '../i18n/loginStrings';

// ── Icons (inline SVG — no extra dependency) ───────────────────────────────────

const EyeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const XIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// ── Brand panel (desktop only) ─────────────────────────────────────────────────

const BrandPanel = ({ t }) => (
  <div
    className="hidden lg:flex flex-col justify-between p-12 text-white"
    style={{ backgroundColor: '#312e81' /* primary-900 — change here to retint */ }}
  >
    {/* Wordmark */}
    <div>
      <div className="flex items-center gap-3 mb-14">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center select-none overflow-hidden">
          <img src="/logo.png" alt="She &amp; She" className="w-full h-full object-contain" />
        </div>
        <span className="text-xl font-bold tracking-tight">{t('brand.product')}</span>
      </div>

      <h2 className="text-4xl font-bold leading-tight mb-4">{t('brand.subtitle')}</h2>
      <p className="text-base leading-relaxed" style={{ color: 'rgba(255,255,255,0.6)' }}>
        {t('brand.descriptor')}
      </p>
    </div>

    {/* Geometric motif — pure CSS circles, no image asset needed */}
    <div
      className="relative h-44 rounded-2xl overflow-hidden"
      style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}
    >
      <div className="absolute rounded-full"
        style={{ width: 200, height: 200, top: -60, right: -40, backgroundColor: 'rgba(255,255,255,0.07)' }} />
      <div className="absolute rounded-full"
        style={{ width: 120, height: 120, bottom: -30, left: 20, backgroundColor: 'rgba(255,255,255,0.05)' }} />
      <div className="absolute rounded-full"
        style={{ width: 60, height: 60, top: 40, left: 80, backgroundColor: 'rgba(255,255,255,0.09)' }} />
    </div>
  </div>
);

// ── Inline error alert ─────────────────────────────────────────────────────────

const ErrorAlert = ({ id, message, onDismiss, dismissLabel }) => (
  <div id={id} className="login-alert" role="alert" aria-live="polite">
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      className="shrink-0 mt-0.5" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
    <span className="flex-1">{message}</span>
    <button
      type="button"
      onClick={onDismiss}
      aria-label={dismissLabel}
      className="shrink-0 text-red-500 hover:text-red-700 transition-colors"
    >
      <XIcon />
    </button>
  </div>
);

// ── Login page ─────────────────────────────────────────────────────────────────

const Login = () => {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd]   = useState(false);
  const [remember, setRemember] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null); // { message, hasFieldError }

  const { login }  = useAuth();
  const navigate   = useNavigate();
  const { t, lang, setLang, isRtl } = useLoginLang();

  const hasFieldError = error?.hasFieldError ?? false;
  const ERROR_ALERT_ID = 'login-error-alert';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const result = await login(email, password, remember);

    if (result.success) {
      toast.success(lang === 'ar' ? 'تم تسجيل الدخول بنجاح' : 'Signed in successfully');
      navigate('/');
    } else {
      const msg = localizeError(result.status, result.message, lang);
      // Show field-level aria-invalid only for credential errors (401/400), not rate-limit/inactive.
      const hasField = result.status === 401 || result.status === 400;
      setError({ message: msg, hasFieldError: hasField });
    }

    setLoading(false);
  };

  const clearError = () => setError(null);

  return (
    <div className="min-h-screen flex" dir={isRtl ? 'rtl' : 'ltr'}>

      {/* ── Form panel ── */}
      <div className="flex flex-1 flex-col justify-center px-6 py-12 lg:px-16 bg-white">

        {/* Top bar: mobile logo + language toggle */}
        <div className="flex items-center justify-between mb-8 lg:mb-0">
          <div className="flex items-center gap-2 lg:hidden">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center select-none overflow-hidden">
              <img src="/logo.png" alt="She &amp; She" className="w-full h-full object-contain" />
            </div>
            <span className="font-bold text-gray-800">She & She</span>
          </div>

          {/* Language toggle — trailing edge in both LTR and RTL */}
          <button
            type="button"
            onClick={() => setLang(lang === 'en' ? 'ar' : 'en')}
            className="ms-auto text-sm font-medium text-primary-600 hover:text-primary-800 transition-colors duration-150"
            aria-label={lang === 'en' ? 'Switch to Arabic' : 'التبديل إلى الإنجليزية'}
          >
            {t('lang.switch')}
          </button>
        </div>

        {/* Form content — centered vertically in the column */}
        <div className="w-full max-w-sm mx-auto mt-10 lg:mt-0">
          <h1 className="text-3xl font-bold text-gray-900 mb-1">{t('form.heading')}</h1>
          <p className="text-sm text-gray-500 mb-8">{t('form.subheading')}</p>

          {/* Inline error — above the form fields */}
          {error && (
            <div className="mb-6">
              <ErrorAlert
                id={ERROR_ALERT_ID}
                message={error.message}
                onDismiss={clearError}
                dismissLabel={t('error.dismiss')}
              />
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate className="space-y-5">

            {/* Email */}
            <div>
              <label htmlFor="login-email" className="block text-sm font-medium text-gray-700 mb-1.5">
                {t('email.label')}
              </label>
              <input
                id="login-email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => { setEmail(e.target.value); clearError(); }}
                className={`input${hasFieldError ? ' input-error' : ''}`}
                placeholder={t('email.placeholder')}
                required
                disabled={loading}
                aria-invalid={hasFieldError ? 'true' : undefined}
                aria-describedby={error ? ERROR_ALERT_ID : undefined}
              />
            </div>

            {/* Password */}
            <div>
              <label htmlFor="login-password" className="block text-sm font-medium text-gray-700 mb-1.5">
                {t('password.label')}
              </label>
              <div className="input-affix-wrap">
                <input
                  id="login-password"
                  type={showPwd ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); clearError(); }}
                  className={`input${hasFieldError ? ' input-error' : ''}`}
                  placeholder={t('password.placeholder')}
                  required
                  disabled={loading}
                  aria-invalid={hasFieldError ? 'true' : undefined}
                />
                <button
                  type="button"
                  className="input-affix-btn"
                  onClick={() => setShowPwd((v) => !v)}
                  aria-label={showPwd ? t('password.hide') : t('password.show')}
                  aria-pressed={showPwd}
                  tabIndex={0}
                >
                  {showPwd ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>

            {/* Remember-me */}
            <div className="flex items-center gap-2.5">
              <input
                id="login-remember"
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
              />
              <label htmlFor="login-remember" className="text-sm text-gray-600 cursor-pointer select-none">
                {t('remember.label')}
              </label>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              aria-busy={loading}
              className="btn-primary w-full py-3 flex items-center justify-center gap-2 mt-2"
            >
              {loading ? (
                <>
                  <div
                    className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
                    aria-hidden="true"
                  />
                  <span>{t('submit.loading')}</span>
                </>
              ) : (
                t('submit.label')
              )}
            </button>
          </form>

          {/* Forgot password — informational only; real reset needs a backend URL change */}
          <p className="mt-7 text-center text-xs text-gray-400">
            {t('forgot.text')}
          </p>
        </div>
      </div>

      {/* ── Brand panel (lg+ only, trailing side) ── */}
      <div className="hidden lg:block lg:w-5/12 xl:w-2/5">
        <BrandPanel t={t} />
      </div>
    </div>
  );
};

export default Login;
